import React from "react";
import { withTranslation } from "react-i18next";
import { createUseStyles } from "react-jss";
import { Visibility, VisibilityOff, Check } from "@mui/icons-material";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import { GROUP_TYPE_COLORS } from "utils/constants";
import { GROUP_TYPE_LABELS } from "utils/constants";

const style = {
    groupCard: {
        minWidth: "200px",
        maxWidth: "300px",
        transition: "all 0.2s ease",
        border: "3px solid transparent",
        borderRadius: "8px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        margin: "6px 0px",
        "&:hover": {
            transform: "translateY(-3px)",
            boxShadow: "0 3px 10px rgba(0,0,0,0.1)",
            cursor: "pointer",
        },
        // Extra contrast only in dark mode (pink --spara-border is too loud in light).
        "[data-theme='dark'] &": {
            background: "var(--spara-surface-2, #171e33)",
            borderColor: "var(--spara-border, #3a4563)",
            boxShadow: "var(--spara-shadow-card, 0 2px 10px rgba(0,0,0,0.4))",
            "&:hover": {
                borderColor: "var(--spara-primary, #e5576c)",
                boxShadow: "var(--spara-shadow, 0 3px 12px rgba(0,0,0,0.45))",
            },
        },
    },
    groupCardSelected: {
        borderColor: "#4caf50",
        boxShadow: "0 4px 14px rgba(76, 175, 80, 0.3)",
    },
    selectedCheckBadge: {
        position: "absolute",
        top: "-10px",
        right: "-10px",
        width: "24px",
        height: "24px",
        borderRadius: "50%",
        backgroundColor: "#4caf50",
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
        zIndex: 2,
        "& svg": {
            fontSize: "15px",
        },
    },
    cardContent: {
        padding: "8px",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        flex: 1,
    },
    logoContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        marginBottom: "2px",
        aspectRatio: 1.7646768867,
        overflow: "hidden",
    },
    logo: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        borderRadius: "4px",
    },
    groupInfo: {
        display: "flex",
        flexDirection: "column",
        gap: "6px",
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
    },
    groupName: {
        fontSize: "0.9rem",
        fontWeight: "bold",
        textAlign: "center",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        lineHeight: "1.2",
        height: "2.4em",
        marginBottom: "2px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--spara-text-strong, inherit)",
    },
    visibilityBadge: {
        position: "absolute",
        top: "-12px",
        left: "5px",
        width: "20px",
        height: "20px",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
        "& svg": {
            fontSize: "14px",
        },
        zIndex: 1,
    },
    visibleBadge: {
        backgroundColor: "#4caf50",
        color: "white",
    },
    hiddenBadge: {
        backgroundColor: "#f44336",
        color: "white",
    },
    groupTypeBadge: {
        position: "absolute",
        top: "-12px",
        left: "28px",
        minWidth: "20px",
        height: "20px",
        borderRadius: "10px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
        padding: "0 6px",
        fontSize: "11px",
        fontWeight: "bold",
        color: "white",
        zIndex: 1,
    },
    separator: {
        width: "100%",
        height: "1px",
        backgroundColor: "var(--spara-border-subtle, rgba(0, 0, 0, 0.1))",
        margin: "4px 0",
    },
};

const useStyles = createUseStyles(style);

const GroupCard = ({ group, onClick, hideBadges = false, isSelected = false, t }) => {
    const classes = useStyles();

    return (
        <Card
            className={`${classes.groupCard} ${isSelected ? classes.groupCardSelected : ''}`}
            onClick={() => onClick(group)}
        >
            {isSelected && (
                <div className={classes.selectedCheckBadge}>
                    <Check />
                </div>
            )}
            {!hideBadges && (
                <div
                    className={`${classes.visibilityBadge} ${
                        group.hidden ? classes.hiddenBadge : classes.visibleBadge
                    }`}
                >
                    {group.hidden ? <VisibilityOff /> : <Visibility />}
                </div>
            )}
            {!hideBadges && group.type && group.type !== "" && (
                <div
                    className={classes.groupTypeBadge}
                    style={{
                        backgroundColor: GROUP_TYPE_COLORS[group.type]
                            ? GROUP_TYPE_COLORS[group.type]
                            : GROUP_TYPE_COLORS.regular,
                    }}
                >
                    {t(GROUP_TYPE_LABELS[group.type])}
                </div>
            )}
            <CardBody className={classes.cardContent}>
                <div className={classes.logoContainer}>
                    {group.logo && group.logo !== "null" ? (
                        <img
                            alt={group.name + " logo"}
                            className={classes.logo}
                            src={group.logo || "/spara-placeholder.png"}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "/spara-placeholder.png";
                            }}
                        />
                    ) : (
                        <div style={{ height: "80px" }} />
                    )}
                </div>
                <div className={classes.separator} />
                <div className={classes.groupInfo}>
                    <div className={classes.groupName}>{group.name}</div>
                </div>
            </CardBody>
        </Card>
    );
};

export default withTranslation("", { withRef: true })(GroupCard);
